/**
 * Maps WooCommerce product categories / _tms_category values
 * to the training certification slugs used in the app.
 *
 * If a WooCommerce category has no specific training, it maps to 'general'.
 * 'general' jobs require only driver-certification to accept.
 */

// Explicit mapping from WooCommerce _tms_category / product category slug → training slug
const CATEGORY_MAP: Record<string, string> = {
  // Peloton bikes → individual product training
  'peloton-bike': 'peloton-bike',
  'peloton-bike-2nd-gen': 'peloton-bike',
  'peloton-bike-3rd-gen': 'peloton-bike',
  'peloton-bike-plus': 'peloton-bike-plus',
  'indoor-bikes': 'peloton-bike',
  'indoor-bike-entry-level': 'peloton-bike',
  'indoor-bike-premium': 'peloton-bike-plus',
  'assault-fitness-bike': 'peloton-bike',
  'echelon-smart-bikes': 'peloton-bike',

  // Peloton treads → individual product training
  'peloton-tread': 'peloton-tread',
  'peloton-tread-plus': 'peloton-tread-plus',

  // Peloton row → individual product training
  'peloton-row': 'peloton-row',

  // Treadmills (non-Peloton) → treadmill training
  'treadmills': 'treadmill',
  'treadmill-entry-level': 'treadmill',
  'treadmill-premium': 'treadmill',

  // Rowers (non-Peloton) → rowing-machine training
  'rower': 'rowing-machine',
  'hydrow-pro-rowing-machine': 'rowing-machine',

  // Tonal → tonal training
  'tonal-home-gym': 'tonal',

  // Sauna → sauna training
  'sauna': 'sauna',

  // Hot tubs → hot-tub training
  'hot-tubs': 'hot-tub',
  'hot-tub': 'hot-tub',
  'swim-spa': 'hot-tub',

  // Appliances → appliance training
  'refrigerators': 'appliance',
  'appliance': 'appliance',
  'general-appliances': 'appliance',
  'ovens': 'appliance',
  'stoves-and-ranges': 'appliance',
  'washing-machines': 'appliance',

  // Cold plunge → cold-plunge training
  'cold-plunge': 'cold-plunge',
  'float-pod': 'cold-plunge',

  // Outdoor → outdoor training
  'play-sets': 'outdoor',
  'swing-set-entry-level': 'outdoor',
  'swing-set-large-premium': 'outdoor',
  'outdoors': 'outdoor',

  // Elliptical / stepper → gym-equipment
  'elliptical': 'gym-equipment',
  'stair-stepper': 'gym-equipment',

  // Pilates reformers (NOT furniture, even though many include an "EXO chair")
  'pilates': 'pilates-reformer',
  'pilates-reformer': 'pilates-reformer',
  'pilates-reformers': 'pilates-reformer',
  'reformer': 'pilates-reformer',
  'reformers': 'pilates-reformer',
  'pilates-equipment': 'pilates-reformer',
  // Wellness → massage-chair
  'wellness': 'massage-chair',
  'massage-chair': 'massage-chair',
  'massage-chairs': 'massage-chair',

  // Cardio (generic) → gym-equipment
  'cardio': 'gym-equipment',

  // Strength / gym equipment → gym-equipment
  'strength': 'gym-equipment',
  'home-gyms': 'gym-equipment',
  'home-gym-entry-level': 'gym-equipment',
  'home-gym-premium': 'gym-equipment',
  'other-gym-equipment': 'gym-equipment',
  'squat-rack': 'gym-equipment',
  'squat-rack-2-post': 'gym-equipment',
  'squat-racks-4-post': 'gym-equipment',
  'bowflex': 'gym-equipment',
  'bowflex-bxt216': 'gym-equipment',
  'bowflex-max-trainer-m9': 'gym-equipment',
  'bowflex-revolution': 'gym-equipment',
  'bowflex-xtreme-2-se': 'gym-equipment',
  'cybex': 'gym-equipment',
  'matrix': 'gym-equipment',
  'matrix-fitness': 'gym-equipment',
  'nordictrack': 'gym-equipment',
  'force-usa': 'gym-equipment',
  'force-usa-g6': 'gym-equipment',
  'force-usa-g10': 'gym-equipment',
  'force-usa-g15': 'gym-equipment',
  'force-usa-g20': 'gym-equipment',
  'hoist': 'gym-equipment',
  'hoist-fitness': 'gym-equipment',
  'parabody': 'gym-equipment',
  'body-solid': 'gym-equipment',
  'body-solid-exm3000lps': 'gym-equipment',
  'balanced-body': 'gym-equipment',
  'clmbr': 'gym-equipment',
  'inspire-fitness': 'gym-equipment',
  'life-fitness': 'gym-equipment',
  'precor': 'gym-equipment',
  'true-fitness': 'gym-equipment',
  'spirit-fitness': 'gym-equipment',
  'sole-fitness': 'gym-equipment',
  'proform': 'gym-equipment',
  'marcy': 'gym-equipment',
  'weider': 'gym-equipment',
  'titan-fitness': 'gym-equipment',
  'rep-fitness': 'gym-equipment',
  'rogue-fitness': 'gym-equipment',
  'gym-equipment': 'gym-equipment',
  'weight-bench': 'gym-equipment',
  'smith-machine': 'gym-equipment',
  'functional-trainer': 'gym-equipment',
  'cable-machine': 'gym-equipment',
  'power-rack': 'gym-equipment',

  // Furniture → furniture
  'furniture': 'furniture',
  'beds': 'furniture',
  'bed': 'furniture',
  'bed-frame': 'furniture',
  'sofas': 'furniture',
  'sofa': 'furniture',
  'couch': 'furniture',
  'tables': 'furniture',
  'table': 'furniture',
  'dining-table': 'furniture',
  'dining-set': 'furniture',
  'dining-sets': 'furniture',
  'dressers': 'furniture',
  'dresser': 'furniture',
  'wardrobes': 'furniture',
  'wardrobe': 'furniture',
  'sectionals': 'furniture',
  'sectional': 'furniture',
  'recliners': 'furniture',
  'recliner': 'furniture',
  'bedroom-sets': 'furniture',
  'bedroom-set': 'furniture',
  'baby-furniture': 'furniture',
  'desks': 'furniture',
  'desk': 'furniture',
  'entertainment-centers': 'furniture',
  'entertainment-center': 'furniture',
  'tv-stands': 'furniture',
  'tv-stand': 'furniture',
  'bookcase': 'furniture',
  'bookcases': 'furniture',
  'cabinet': 'furniture',
  'cabinets': 'furniture',
  'nightstand': 'furniture',
  'nightstands': 'furniture',
  'mattress': 'furniture',
  'mattresses': 'furniture',
  'bunk-bed': 'furniture',
  'bunk-beds': 'furniture',
  'loveseat': 'furniture',
  'futon': 'furniture',
  'ottoman': 'furniture',
  'armoire': 'furniture',
  'vanity': 'furniture',
  'china-cabinet': 'furniture',
  'hutch': 'furniture',
  'console-table': 'furniture',
  'coffee-table': 'furniture',
  'end-table': 'furniture',
  'office-furniture': 'furniture',
  'patio-furniture': 'furniture',
  'outdoor-furniture': 'furniture',
  'chair': 'furniture',
  'chairs': 'furniture',

  // Electronics → electronics
  'electronics': 'electronics',
  'tvs': 'electronics',
  'tv': 'electronics',
  'television': 'electronics',
  'cameras': 'electronics',
  'camera': 'electronics',
  'desktop-computers': 'electronics',
  'desktop-computer': 'electronics',
  'drones': 'electronics',
  'drone': 'electronics',
  'smartphones': 'electronics',
  'smartphone': 'electronics',
  'tablets': 'electronics',
  'tablet': 'electronics',
  'hi-fi': 'electronics',
  'hi-fi-music-systems': 'electronics',
  'music-systems': 'electronics',
  'workstations': 'electronics',
  'workstation': 'electronics',
  'laptops': 'electronics',
  'laptop': 'electronics',
  'monitors': 'electronics',
  'monitor': 'electronics',
  'home-theater': 'electronics',
  'speakers': 'electronics',
  'soundbar': 'electronics',
  'gaming-console': 'electronics',
  'projector': 'electronics',

  // Golf carts (titled vehicles, NOT simulators)
  'golf-cart': 'golf-cart',
  'golf-carts': 'golf-cart',
  'club-car': 'golf-cart',
  'ezgo': 'golf-cart',
  'lsv': 'golf-cart',
  // Golf simulators → golf-simulator
  'golf-simulator': 'golf-simulator',
  'golf-simulators': 'golf-simulator',
  'golf': 'golf-simulator',

  // Pool tables / game room → pool-table
  'pool-table': 'pool-table',
  'pool-tables': 'pool-table',
  'billiards': 'pool-table',
  'foosball': 'pool-table',
  'ping-pong': 'pool-table',
  'table-tennis': 'pool-table',
  'arcade': 'pool-table',
  'game-room': 'pool-table',
  'shuffleboard': 'pool-table',
  'air-hockey': 'pool-table',

  // Vehicles — a titled, drivable vehicle is its own thing, NOT a generic delivery.
  'cars': 'vehicle',
  'car': 'vehicle',
  'vehicles': 'vehicle',
  'vehicle': 'vehicle',

  // ─── Commonplace v2 taxonomy ────────────────────────────────────────────────
  // MIRROR of packages/backend/src/utils/categoryMapping.ts — keep in sync.
  // v2 is the source of orders after the WooCommerce cutover; every v2 slug is
  // listed explicitly so a category is never left to keyword guessing.
  'peloton': 'peloton-bike',
  'tonal': 'tonal',
  'adjustable-dumbbells': 'gym-equipment',
  'spin-bike': 'gym-equipment',
  'recumbent-bike': 'gym-equipment',
  'air-bike': 'gym-equipment',
  'stair-climber': 'gym-equipment',
  'home-gym': 'gym-equipment',
  'medical-spa': 'medical-spa',
  'washer': 'appliance',
  'dishwasher': 'appliance',
  'electric-range': 'appliance',
  'dining-tables': 'furniture',
  'coffee-tables': 'furniture',
  'bookshelves': 'furniture',
  'tv-console': 'furniture',
  'mirrors': 'furniture',
  'lamps': 'furniture',
  // ('golf-carts' is already mapped in the Woo block above)
  'e-bike': 'bicycle',
  'bikes': 'bicycle',
  'scooters': 'vehicle',
  'atv': 'vehicle',
  'rv-motorhome': 'vehicle',
  'boats': 'watercraft',
  'jet-skis': 'watercraft',
  'playsets': 'outdoor',
  'shed': 'outdoor',
  'grill': 'outdoor',
  'lawn-mower': 'lawn-equipment',
  'tractors': 'lawn-equipment',

  // Everything else defaults to 'general' via the function below
};

/**
 * Map a WooCommerce category slug to a training certification slug.
 * Uses explicit map first, then keyword matching, then defaults to 'general'.
 */
/**
 * Normalize a slug or free-text title into a padded stream of words, so keyword
 * matching below is WORD-based rather than SUBSTRING-based. Substring matching
 * produced real miscategorizations: 'atv' contains 'tv' (→ electronics),
 * 'adjustable-dumbbells' contains 'table' (→ furniture), 'brown sofa' contains
 * 'row' (→ rowing machine). '+' is kept because it is semantic (Bike+ / Tread+).
 */
function words(s: string): string {
  return ' ' + s.toLowerCase().replace(/[^a-z0-9+]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
}

/** True when any term appears as a WHOLE word (singular or plural) in the word stream. */
function hasAny(n: string, terms: string[]): boolean {
  return terms.some((t) => n.includes(` ${t} `) || n.includes(` ${t}s `) || n.includes(` ${t}es `));
}

export function mapToTrainingCategory(wooCategory: string): string {
  if (!wooCategory) return 'general';

  const lower = wooCategory.toLowerCase().trim();

  // Check explicit map first
  if (CATEGORY_MAP[lower]) return CATEGORY_MAP[lower];

  const n = words(lower);
  const isPeloton = hasAny(n, ['peloton']);

  if (isPeloton && (n.includes(' tread+ ') || n.includes(' tread plus '))) return 'peloton-tread-plus';
  if (isPeloton && hasAny(n, ['tread', 'treadmill'])) return 'peloton-tread';
  if (isPeloton && (n.includes(' bike+ ') || n.includes(' bike plus '))) return 'peloton-bike-plus';
  if (isPeloton && hasAny(n, ['bike'])) return 'peloton-bike';
  if (isPeloton && hasAny(n, ['row', 'rower'])) return 'peloton-row';
  if (isPeloton) return 'peloton-bike'; // default peloton

  if (hasAny(n, ['tread', 'treadmill'])) return 'treadmill';
  if (hasAny(n, ['row', 'rower', 'rowing'])) return 'rowing-machine';
  if (hasAny(n, ['tonal'])) return 'tonal';
  if (hasAny(n, ['sauna'])) return 'sauna';
  if (hasAny(n, ['hot tub', 'hottub', 'swim spa'])) return 'hot-tub';
  if (hasAny(n, ['cold plunge', 'plunge', 'float pod'])) return 'cold-plunge';
  // Pilates reformer must beat the 'chair'/'table' furniture words below.
  if (hasAny(n, ['pilates', 'reformer'])) return 'pilates-reformer';
  if (hasAny(n, ['massage chair', 'massage'])) return 'massage-chair';
  if (hasAny(n, ['medical spa', 'med spa'])) return 'medical-spa';

  // A golf CART (titled, drivable vehicle) is NOT a golf simulator. Match it first.
  if (hasAny(n, ['golf cart', 'club car', 'ez go', 'ezgo', 'e z go', 'lsv', 'yamaha drive', 'advanced ev'])) return 'golf-cart';
  if (hasAny(n, ['golf', 'simulator'])) return 'golf-simulator';
  if (hasAny(n, ['pool table', 'billiard', 'foosball', 'arcade', 'shuffleboard', 'air hockey', 'ping pong', 'table tennis'])) return 'pool-table';

  // Bicycles / e-bikes are VEHICLES, not gym equipment — they beat the generic
  // 'bike' → gym-equipment rule below (that rule is for spin/exercise bikes).
  if (hasAny(n, ['e bike', 'ebike', 'bicycle', 'mountain bike', 'mtb', 'road bike', 'cruiser bike'])) return 'bicycle';
  if (hasAny(n, ['atv', 'utv', 'side by side', 'motorcycle', 'moped', 'scooter', 'vespa', 'dirt bike',
                 'rv', 'motorhome', 'camper', 'truck', 'suv', 'sedan', 'minivan', 'car'])) return 'vehicle';
  if (hasAny(n, ['boat', 'jet ski', 'jetski', 'waverunner', 'sea doo', 'seadoo', 'pontoon', 'kayak', 'canoe'])) return 'watercraft';
  if (hasAny(n, ['lawn mower', 'mower', 'tractor', 'zero turn', 'snow blower', 'leaf blower', 'chainsaw', 'tiller'])) return 'lawn-equipment';
  if (hasAny(n, ['play set', 'playset', 'swing set', 'playground', 'trampoline', 'shed', 'gazebo', 'pergola', 'grill', 'smoker'])) return 'outdoor';

  if (hasAny(n, ['refrigerator', 'fridge', 'appliance', 'washer', 'dryer', 'oven', 'dishwasher', 'stove', 'range', 'freezer'])) return 'appliance';
  // A TV console / TV stand is FURNITURE — check it before the 'tv' electronics rule.
  if (hasAny(n, ['tv console', 'tv stand', 'tv unit', 'media console', 'entertainment center'])) return 'furniture';
  if (hasAny(n, ['chair', 'sofa', 'couch', 'loveseat', 'bed', 'mattress', 'dresser', 'table', 'desk', 'wardrobe',
                 'sectional', 'recliner', 'furniture', 'cabinet', 'bookcase', 'bookshelf', 'shelf', 'shelving',
                 'nightstand', 'credenza', 'buffet', 'mirror', 'lamp', 'ottoman', 'futon', 'armoire'])) return 'furniture';
  if (hasAny(n, ['tv', 'television', 'computer', 'laptop', 'monitor', 'drone', 'camera', 'speaker', 'soundbar', 'projector', 'hi fi'])) return 'electronics';
  if (hasAny(n, ['bowflex', 'cybex', 'matrix', 'nordictrack', 'force usa', 'hoist', 'body solid', 'squat', 'smith machine',
                 'gym', 'bench press', 'power rack', 'squat rack', 'dumbbell', 'kettlebell', 'elliptical', 'stair climber',
                 'stair stepper', 'bike', 'cycle', 'spin'])) return 'gym-equipment';

  return 'general';
}

/**
 * Get the display name for a training category slug.
 */
export function getTrainingCategoryLabel(trainingSlug: string): string {
  const labels: Record<string, string> = {
    'driver-certification': 'Driver Certification',
    'peloton-bike': 'Peloton Bike',
    'peloton-bike-plus': 'Peloton Bike+',
    'peloton-tread': 'Peloton Tread',
    'peloton-tread-plus': 'Peloton Tread+',
    'peloton-row': 'Peloton Row',
    'fitness-equipment': 'Peloton Equipment',
    'treadmill': 'Treadmills',
    'rowing-machine': 'Rowing Machines',
    'tonal': 'Tonal',
    'sauna': 'Saunas',
    'hot-tub': 'Hot Tubs',
    'appliance': 'Appliances',
    'cold-plunge': 'Cold Plunge',
    'outdoor': 'Outdoor Equipment',
    'general': 'General Delivery',
    'furniture': 'Furniture',
    'gym-equipment': 'Gym Equipment',
    'electronics': 'Electronics & TVs',
    'golf-cart': 'Golf Cart',
    'golf-simulator': 'Golf Simulators',
    'pool-table': 'Game Room',
    'massage-chair': 'Massage Chairs',
    'pilates-reformer': 'Pilates Reformer',
    'vehicle': 'Vehicles',
    'bicycle': 'Bicycles & E-Bikes',
    'watercraft': 'Boats & Watercraft',
    'lawn-equipment': 'Lawn & Yard Equipment',
    'medical-spa': 'Medical Spa Equipment',
    'palletizing': 'Palletizing',
  };
  return labels[trainingSlug] || trainingSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Check if a driver is certified for a specific job based on their certifications.
 * 'general' category jobs only require driver-certification.
 * All other categories require their specific training + driver-certification.
 */
/**
 * A palletize freight job (our crew builds the pallet) also needs the palletizing certification
 * (owner, 3 Nov). Collecting an already built pallet for delivery does not. Same rule as the
 * backend's accept gate (dispatch.service acceptJob, lib/palletizingCourse PALLETIZE_MODES).
 */
export function needsPalletizingCert(job: any): boolean {
  const mode = job?.freight?.mode;
  return mode === 'PALLETIZE_DROP' || mode === 'PALLETIZE_HOLD';
}

export function isDriverCertifiedForJob(
  jobTrainingCategory: string,
  certifications: string[]
): boolean {
  const hasDriverCert = certifications.includes('driver-certification');

  // General and Store-to-Sell "storage" jobs only need the base driver certification.
  if (jobTrainingCategory === 'general' || jobTrainingCategory === 'storage') return hasDriverCert;

  // A category can only be GATED if a training course actually exists to un-gate it.
  //
  // This was a live dead end: golf-cart and pilates-reformer jobs (and, after the v2
  // cutover, every new category) demanded a certification with no course behind it.
  // The driver got "Certification Required" → "Go to Training" → the driver-cert quiz
  // they had already passed → still not certified. The job could NEVER be accepted.
  //
  // Deriving the gate from getCourseIdForCategory keeps this self-maintaining: publish
  // a course for a category and that category starts gating automatically.
  if (!getCourseIdForCategory(jobTrainingCategory)) return hasDriverCert;

  // Specific category jobs need both driver cert AND category cert
  return hasDriverCert && certifications.includes(jobTrainingCategory);
}

/**
 * Get the course ID that a driver should take for a given training category.
 * Returns null if no matching course exists.
 */
export function getCourseIdForCategory(trainingCategory: string): string | null {
  const courseMap: Record<string, string> = {
    'driver-certification': 'course-driver-cert',
    'palletizing': 'course-palletizing',
    'peloton-bike': 'course-peloton-bike',
    'peloton-bike-plus': 'course-peloton-bike-plus',
    'peloton-tread': 'course-peloton-tread',
    'peloton-tread-plus': 'course-peloton-tread-plus',
    'peloton-row': 'course-peloton-row',
    'fitness-equipment': 'course-peloton-bike',
    'sauna': 'course-sauna',
    'tonal': 'course-tonal',
    'hot-tub': 'course-hot-tub',
    'treadmill': 'course-treadmill',
    'rowing-machine': 'course-rowing-machine',
    // 'general' removed — covered by driver-certification only
    'appliance': 'course-appliance',
    'cold-plunge': 'course-cold-plunge',
    'outdoor': 'course-outdoor',
    'furniture': 'course-furniture',
    'gym-equipment': 'course-gym-equipment',
    'electronics': 'course-electronics',
    'golf-simulator': 'course-golf-simulator',
    'pool-table': 'course-pool-table',
    'massage-chair': 'course-massage-chair',
  };
  return courseMap[trainingCategory] || null;
}

/**
 * Get all unique training categories that exist in the system.
 */
export function getAllTrainingCategories(): string[] {
  return [
    'driver-certification',
    'peloton-bike',
    'peloton-bike-plus',
    'peloton-tread',
    'peloton-tread-plus',
    'peloton-row',
    'sauna',
    'tonal',
    'hot-tub',
    'treadmill',
    'rowing-machine',
    'general',
    'appliance',
    'cold-plunge',
    'outdoor',
    'furniture',
    'gym-equipment',
    'electronics',
    'golf-simulator',
    'pool-table',
    'massage-chair',
  ];
}
